import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AppIcon } from '@capacitor-community/app-icon';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AlertController, Platform } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { OpenOptions } from 'capacitor-email-composer';
import { compact, isEmpty } from 'lodash-es';
import { combineLatest, Observable, tap, withLatestFrom } from 'rxjs';

import { virtualCurrencyLabel } from '../../constants/monetization.constant';
import { CoreConfig } from '../../core.module';
import { GenericComponent } from '../../misc/components/generic/generic.component';
import { EmailComposerService } from '../../misc/services/email-composer.service';
import { LoadingService } from '../../misc/services/loading.service';
import { LoggerService } from '../../misc/services/logger.service';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromMonetizationActions from '../../store/actions/monetization.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromUserActions from '../../store/actions/user.actions';
import * as fromDeviceSelectors from '../../store/selectors/device.selectors';
import * as fromLayoutSelectors from '../../store/selectors/layout.selectors';
import * as fromMonetizationSelectors from '../../store/selectors/monetization.selectors';
import * as fromOfficeSelectors from '../../store/selectors/office.selectors';
import * as fromOrganizationSelectors from '../../store/selectors/organization.selectors';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import * as fromTitleRepSelectors from '../../store/selectors/title-rep.selectors';
import * as fromUserSelectors from '../../store/selectors/user.selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-home',
  standalone: false,
  templateUrl: './home.page.html'
})
export class HomePageComponent extends GenericComponent {
  private coreConfig = inject<CoreConfig>(CoreConfig);
  private alertController = inject(AlertController);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private emailComposerService = inject(EmailComposerService);
  private loadingService = inject(LoadingService);
  private loggerService = inject(LoggerService);
  private monetizationService = inject(MonetizationService);
  private platform = inject(Platform);
  private router = inject(Router);
  private store = inject(Store);

  tutorialsUrl = 'https://redshedtechnology.com/sdtutorials';
  showChangeIconOption = false;
  disableChangeIconOption = true;
  hasDefaultIcon = true;
  applicationName: string | null = null;
  supportOpenOptions: OpenOptions | null = null;
  orderTitleOpenOptions: OpenOptions | null = null;

  isDebugModeEnabled = false;
  debugMode$: Observable<boolean>;
  debugCounter = 0;

  useMobileLayout = false;
  environment: string;

  useMobileLayout$: Observable<boolean>;
  appVersion$: Observable<string>;
  calculatorsEnabled$: Observable<boolean>;
  netSheetEnabled$: Observable<boolean>;
  orderTitleEnabled$: Observable<boolean>;
  farmAlertsEnabled$: Observable<boolean>;
  virtualCurrency$: Observable<number>;
  monetizationEnabled$: Observable<boolean>;
  primarySubscriptionEnabled$: Observable<boolean>;
  primarySubscriptionActive$: Observable<boolean>;
  primarySubscriptionName$: Observable<string>;
  isNativePlatform = Capacitor.isNativePlatform();

  enableTooltips$: Observable<boolean>;

  constructor() {
    super();
    this.environment = this.coreConfig.production ? 'Release' : 'Test';
    this.debugMode$ = this.store.pipe(
      select(fromSettingSelectors.selectIsDebugModeEnabled),
      tap((isDebugModeEnabled) => {
        this.isDebugModeEnabled = isDebugModeEnabled;
      })
    );
    this.tutorialsUrl = this.coreConfig.tutorialsUrl;
    this.showChangeIconOption = Capacitor.isNativePlatform() && this.platform.is('ios');
    this.useMobileLayout$ = this.store.pipe(
      select(fromLayoutSelectors.selectUseMobileLayout),
      tap((useMobileLayout) => {
        this.useMobileLayout = useMobileLayout;
      })
    );

    this.appVersion$ = this.store.pipe(select(fromDeviceSelectors.selectAppVersion));
    this.calculatorsEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectCalculatorsEnabled));
    this.netSheetEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectNetSheetsEnabled));
    this.orderTitleEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEnabled));
    this.farmAlertsEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsEnabled));
    this.monetizationEnabled$ = this.store.pipe(select(fromMonetizationSelectors.selectIsEnabled));
    this.virtualCurrency$ = this.store.pipe(select(fromMonetizationSelectors.selectVirtualCurrency));
    this.primarySubscriptionEnabled$ = this.store.pipe(
      select(fromMonetizationSelectors.selectPrimarySubscriptionIsEnabled)
    );
    this.primarySubscriptionActive$ = this.store.pipe(
      select(fromMonetizationSelectors.selectPrimarySubscriptionIsActive)
    );
    this.primarySubscriptionName$ = this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionName));

    this.enableTooltips$ = this.store.pipe(select(fromSettingSelectors.selectEnableTooltips));
  }

  async ionViewDidEnter() {
    this.store.dispatch(fromUserActions.reloadMobileUser());
    this.addUniqueSubscription(
      'supportOpenOptionsUpdate',
      this.store
        .pipe(
          select(fromUserSelectors.selectDetails),
          withLatestFrom(
            this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
            this.store.pipe(select(fromOrganizationSelectors.selectApplicationName))
          )
        )
        .subscribe(([userDetails, deviceData, applicationName]) => {
          this.supportOpenOptions = {
            body: '\n\n=======\n' + deviceData + '\n=======',
            cc: [userDetails.email || ''],
            subject: `${applicationName ?? 'SmartDirect&#174; Mobile'}: App Inquiry`,
            to: ['support@redshedtechnology.com']
          };
        })
    );

    this.addUniqueSubscription(
      'orderTitleOpenOptionsUpdate',
      combineLatest([
        this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEnabled)),
        this.store.pipe(select(fromOfficeSelectors.selectOrderTitleEmail)),
        this.store.pipe(select(fromUserSelectors.selectDetails)),
        this.store.pipe(select(fromOrganizationSelectors.selectApplicationName)),
        this.store.pipe(select(fromTitleRepSelectors.selectOrderTitleCCEnabled)),
        this.store.pipe(select(fromTitleRepSelectors.selectEmail))
      ]).subscribe(
        ([orderTitleEnabled, orderTitleEmail, userDetails, applicationName, orderTitleCCEnabled, titleRepEmail]) => {
          if (orderTitleEnabled) {
            this.orderTitleOpenOptions = {
              body: compact([
                'I would like to initiate an order.\n',
                '\n',
                `${userDetails.firstName} ${userDetails.lastName}`,
                userDetails.company,
                userDetails.phone,
                userDetails.email
              ]).join('\n'),
              cc: orderTitleCCEnabled && titleRepEmail ? [titleRepEmail] : [],
              isHtml: false,
              subject: `${applicationName ?? 'SmartDirect&#174; Mobile'}: Order Title`,
              to: [orderTitleEmail || '']
            };
          } else {
            this.orderTitleOpenOptions = null;
          }
        }
      )
    );

    this.addUniqueSubscription(
      'selectApplicationName',
      this.store
        .pipe(select(fromOrganizationSelectors.selectAlternateAppIconName))
        .subscribe(async (alternateAppIconName) => {
          if (this.showChangeIconOption) {
            let supported = false;
            try {
              const { value } = await AppIcon.isSupported();
              supported = value;
            } catch {
              this.loggerService.handleError(new Error('Capacitor App Icon plugin failure (isSupported)'));
            }
            this.disableChangeIconOption = !supported || isEmpty(alternateAppIconName);
            this.changeDetectorRef.detectChanges();
          }
        })
    );

    this.addUniqueSubscription(
      'selectApplicationName',
      this.store.pipe(select(fromOrganizationSelectors.selectApplicationName)).subscribe((applicationName) => {
        this.applicationName = applicationName;
      })
    );

    this.addUniqueSubscription(
      'selectHasDefaultIcon',
      this.store.pipe(select(fromSettingSelectors.selectAlternateAppIconUsed)).subscribe((alternateAppIconUsed) => {
        this.hasDefaultIcon = !alternateAppIconUsed;
        this.changeDetectorRef.detectChanges();
      })
    );
  }

  async changeIcon(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    if (this.showChangeIconOption && !this.disableChangeIconOption) {
      let alert: HTMLIonAlertElement;
      if (this.hasDefaultIcon) {
        alert = await this.alertController.create({
          backdropDismiss: false,
          buttons: [
            {
              role: 'cancel',
              text: 'Cancel'
            },
            {
              handler: () =>
                this.store.dispatch(fromSettingActions.updateApplicationIcon({ payload: { useDefault: false } })),
              text: 'Switch to custom app icon'
            }
          ],
          header: `${this.applicationName || ''}`,
          message: `You are currently using the default application icon. Would you like to use the ${
            this.applicationName || 'alternate'
          } application icon instead? You can return to the default icon anytime.`
        });
      } else {
        alert = await this.alertController.create({
          backdropDismiss: false,
          buttons: [
            {
              role: 'cancel',
              text: 'Cancel'
            },
            {
              handler: () =>
                this.store.dispatch(fromSettingActions.updateApplicationIcon({ payload: { useDefault: true } })),
              text: 'Revert to default app icon'
            }
          ],
          header: 'Default App Icon',
          message: `You are currently using the ${
            this.applicationName || 'alternate'
          } application icon. Would you like to revert to the default icon?`
        });
      }
      await alert.present();
      this.changeDetectorRef.detectChanges();
    }
    return false;
  }

  async reset() {
    const alert = await this.alertController.create({
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          role: 'destructive',
          text: this.useMobileLayout ? 'Reset' : 'Logout'
        }
      ],
      header: this.useMobileLayout ? 'Reset Application' : 'Logout Application',
      message:
        'Are you sure ? You will clear all the application data and you will have to follow the onboarding steps again.'
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();
    if (role === 'destructive') {
      this.store.dispatch(fromSettingActions.resetApplication());
    }
  }

  async openUrl(url: string) {
    await Browser.open({
      url,
      windowName: '_blank'
    });
  }

  async openSupport() {
    if (this.supportOpenOptions) {
      await this.emailComposerService.composeEmail(this.supportOpenOptions);
    }
  }

  async openOrderTitle() {
    if (this.orderTitleOpenOptions) {
      await this.emailComposerService.composeEmail(this.orderTitleOpenOptions);
    }
  }

  async toggleDebugMode() {
    this.debugCounter++;
    if (this.debugCounter > 5) {
      this.debugCounter = 0;
      const alert = await this.alertController.create({
        backdropDismiss: false,
        buttons: [
          {
            role: 'cancel',
            text: 'Ok'
          }
        ],
        header: `${this.isDebugModeEnabled ? 'Normal' : 'Debug'} Mode`,
        message: `You have toggled ${this.isDebugModeEnabled ? 'off' : 'on'} the debug mode.`
      });
      this.store.dispatch(fromSettingActions.toggleDebugMode());
      await alert.present();
    }
  }

  async subscribe(isPrimarySubscriptionActive: boolean | null) {
    if (isPrimarySubscriptionActive === false) {
      await this.monetizationService.showPrimarySubscriptionPlanPaywall();
    }
  }

  async restorePurchases() {
    const alert = await this.alertController.create({
      backdropDismiss: false,
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          handler: () => {
            this.store.dispatch(fromMonetizationActions.restorePurchases());
          },
          role: 'confirm',
          text: 'Restore'
        }
      ],
      header: `Restore Purchases`,
      message: `If you used multiple accounts with this application this will attempt to sync your purchase history. Subscriptions are bound to a single user and will not be transferred.`
    });
    await alert.present();
  }

  toggleTooltips(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    this.store.dispatch(fromSettingActions.toggleEnableTooltips());
  }

  protected readonly virtualCurrencyLabel = virtualCurrencyLabel;
}
